/**
 * 录制状态 Store（Zustand）
 * 管理录制会话、录制列表和录制回放状态
 */
import { create } from 'zustand';
import type { RecordingInfo, TelemetryFrame } from '@fluxlab/protocols';

type RecordingState = 'idle' | 'recording' | 'stopped' | 'loading' | 'playing' | 'paused';
type ExportFormat = 'csv' | 'json';

interface RecordingStore {
  state: RecordingState;
  recordings: RecordingInfo[];
  /** Currently loaded playback frames */
  playbackFrames: TelemetryFrame[];
  playbackRecordingId: string | null;
  /** Current playback frame index */
  playbackIndex: number;
  /** Playback speed multiplier */
  playbackSpeed: number;
  /** Active recording id */
  activeRecordingId: string | null;
  /** Export dialog open */
  exportDialogOpen: boolean;
  /** Selected recording for export */
  exportTarget: RecordingInfo | null;
  /** Sample rate for new recording */
  sampleRate: number;
  /** Recording label */
  label: string;

  setState: (state: RecordingState) => void;
  setRecordings: (recordings: RecordingInfo[]) => void;
  addRecording: (recording: RecordingInfo) => void;
  removeRecording: (id: string) => void;
  updateRecording: (id: string, updates: Partial<RecordingInfo>) => void;
  beginPlayback: (id: string) => void;
  appendPlaybackFrames: (frames: TelemetryFrame[]) => void;
  completePlayback: () => void;
  setPlaybackFrames: (frames: TelemetryFrame[]) => void;
  setPlaybackIndex: (index: number) => void;
  setPlaybackSpeed: (speed: number) => void;
  setActiveRecordingId: (id: string | null) => void;
  setExportDialogOpen: (open: boolean) => void;
  setExportTarget: (target: RecordingInfo | null) => void;
  setSampleRate: (rate: number) => void;
  setLabel: (label: string) => void;
  resetPlayback: () => void;
}

/** 录制状态 Store（Zustand）：管理录制会话、录制列表与回放状态 */
export const useRecordingStore = create<RecordingStore>((set) => ({
  state: 'idle',
  recordings: [],
  playbackFrames: [],
  playbackRecordingId: null,
  playbackIndex: 0,
  playbackSpeed: 1,
  activeRecordingId: null,
  exportDialogOpen: false,
  exportTarget: null,
  sampleRate: 1000,
  label: '',

  setState: (state) => set({ state }),
  setRecordings: (recordings) => set({ recordings }),
  addRecording: (recording) =>
    set((s) => ({ recordings: [recording, ...s.recordings] })),
  removeRecording: (id) =>
    set((s) => ({ recordings: s.recordings.filter((r) => r.id !== id) })),
  updateRecording: (id, updates) =>
    set((s) => ({
      recordings: s.recordings.map((r) => (r.id === id ? { ...r, ...updates } : r)),
    })),
  beginPlayback: (id) => set({ state: 'loading', playbackRecordingId: id, playbackFrames: [], playbackIndex: 0 }),
  appendPlaybackFrames: (frames) => set((s) => ({ playbackFrames: [...s.playbackFrames, ...frames] })),
  completePlayback: () => set({ state: 'paused', playbackIndex: 0 }),
  setPlaybackFrames: (frames) => set({ playbackFrames: frames }),
  setPlaybackIndex: (index) => set({ playbackIndex: index }),
  setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),
  setActiveRecordingId: (id) => set({ activeRecordingId: id }),
  setExportDialogOpen: (open) => set({ exportDialogOpen: open }),
  setExportTarget: (target) => set({ exportTarget: target }),
  setSampleRate: (rate) => set({ sampleRate: rate }),
  setLabel: (label) => set({ label }),
  resetPlayback: () => set({ playbackFrames: [], playbackRecordingId: null, playbackIndex: 0, state: 'stopped' }),
}));

export type { RecordingState, ExportFormat };
